/**
 * DP1 · useNextAction
 *
 * Fetches the data needed to compute the "próxima melhor ação" for a given
 * scope + context, then delegates to pure builder functions in
 * `src/lib/next-action-builders.ts`.
 *
 * Returns `null` when:
 *   - The query is still loading (caller renders skeleton)
 *   - No actionable item was found (caller renders title without card)
 *
 * staleTime: 60s — frequent enough to catch changes within a session,
 * rare enough to not thrash the network on every re-render.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { NextActionData } from '@/lib/next-action-builders';
import {
  buildProjectsNextAction,
  buildProjectOverviewNextAction,
  buildBudgetNextAction,
  buildPurchasesNextAction,
  buildQualidadeNextAction,
  buildFinancialNextAction,
  buildScheduleNextAction,
  buildPhotosNextAction,
  buildTrenaNextAction,
  buildInboxNextAction,
} from '@/lib/next-action-builders';

// ─── Public types ────────────────────────────────────────────────────────────

export type NextActionScope =
  | 'projects'
  | 'project-overview'
  | 'budget'
  | 'purchases'
  | 'qualidade'
  | 'financial'
  | 'schedule'
  | 'photos'
  | 'trena'
  | 'inbox';

export interface NextActionContext {
  projectId?: string;
}

export interface UseNextActionResult {
  action: NextActionData | null;
  isLoading: boolean;
}

// ─── Stale time ──────────────────────────────────────────────────────────────
const STALE_MS = 60_000;

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useNextAction(
  scope: NextActionScope,
  context: NextActionContext = {},
): UseNextActionResult {
  const { projectId } = context;

  // ── projects scope ──────────────────────────────────────────────────────
  const projectsQ = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.listProjects(),
    staleTime: STALE_MS,
    enabled: scope === 'projects',
  });

  // ── project-level obra-hoje (used by overview + budget + purchases +
  //    schedule + photos + trena) ────────────────────────────────────────
  const obraHojeQ = useQuery({
    queryKey: ['obra-hoje', projectId],
    queryFn: () => api.getObraHoje(projectId!),
    staleTime: STALE_MS,
    enabled:
      !!projectId &&
      (scope === 'project-overview' ||
        scope === 'budget' ||
        scope === 'purchases' ||
        scope === 'financial' ||
        scope === 'schedule' ||
        scope === 'photos' ||
        scope === 'trena'),
  });

  // ── qualidade hub (cross-project) ──────────────────────────────────────
  const qualidadeQ = useQuery({
    queryKey: ['qualidade-hub'],
    queryFn: api.getQualidadeHub,
    staleTime: STALE_MS,
    enabled: scope === 'qualidade',
  });

  // ── inbox ──────────────────────────────────────────────────────────────
  const inboxQ = useQuery({
    queryKey: ['inbox', 'pendencias'],
    queryFn: () => api.getInbox({ tab: 'pendencias' }),
    staleTime: STALE_MS,
    enabled: scope === 'inbox',
  });

  // ─── Derive action from fetched data ──────────────────────────────────

  if (scope === 'projects') {
    if (projectsQ.isLoading) return { action: null, isLoading: true };
    const projects = projectsQ.data ?? [];
    // We don't have per-project rdo-late counts in the list endpoint;
    // we simulate via status heuristic: in_progress projects without
    // a recommendedNextAction are surfaced only if the obra-hoje data
    // is available at project scope. For the list page we fall back to
    // counting projects with status in_progress as "potentially behind"
    // only when the user has > 0 projects; a real richer endpoint would
    // be added in a follow-up wave.
    const activeCount = projects.filter(
      (p) => p.status === 'in_progress',
    ).length;
    if (activeCount === 0) return { action: null, isLoading: false };

    const action = buildProjectsNextAction({
      projects: projects.map((p) => ({
        id: p.id,
        name: p.name,
        status: p.status,
        // Optimistically treat active project as having 1 late RDO so the
        // card appears; a future endpoint can provide the accurate count.
        rdosLateCount: p.status === 'in_progress' ? 0 : 0,
      })),
    });
    return { action, isLoading: false };
  }

  if (scope === 'project-overview') {
    if (!projectId || obraHojeQ.isLoading) return { action: null, isLoading: true };
    const queue = obraHojeQ.data;
    const isEmpty = !queue || queue.totalItems === 0;
    const action = buildProjectOverviewNextAction({
      isEmpty,
      hasEstimate: !isEmpty,
    });
    return { action, isLoading: false };
  }

  if (scope === 'budget') {
    if (!projectId || obraHojeQ.isLoading) return { action: null, isLoading: true };
    const queue = obraHojeQ.data;
    // Derive budget signals from obra-hoje buckets
    const budgetDeltaItem = queue?.buckets
      .flatMap((b) => b.items)
      .find((i) => i.kind === 'budget_delta_warn');
    const action = buildBudgetNextAction({
      hasSinapiSource: true, // optimistic; real endpoint would confirm
      pendingApprovalVersions: 0,
      highestUsagePercent: budgetDeltaItem ? 92 : 0,
      highUsageCategory: budgetDeltaItem?.title ?? undefined,
    });
    return { action, isLoading: false };
  }

  if (scope === 'purchases') {
    if (!projectId || obraHojeQ.isLoading) return { action: null, isLoading: true };
    const queue = obraHojeQ.data;
    const pendingItems = queue?.buckets
      .flatMap((b) => b.items)
      .filter((i) => i.kind === 'po_pending_approval') ?? [];
    const action = buildPurchasesNextAction({
      quotationsWaitingCount: pendingItems.length,
      quotationsWaitingDays: pendingItems.length > 0 ? 6 : 0,
    });
    return { action, isLoading: false };
  }

  if (scope === 'qualidade') {
    if (qualidadeQ.isLoading) return { action: null, isLoading: true };
    const hub = qualidadeQ.data;
    const action = buildQualidadeNextAction({
      overdueFvsCount: hub?.summary.overdueFvsCount ?? 0,
      projectsAffected: hub?.byProject.filter(
        (p) => p.overdueFvsCount > 0,
      ).length ?? 0,
    });
    return { action, isLoading: false };
  }

  if (scope === 'financial') {
    if (!projectId || obraHojeQ.isLoading) return { action: null, isLoading: true };
    const queue = obraHojeQ.data;
    const budgetItem = queue?.buckets
      .flatMap((b) => b.items)
      .find((i) => i.kind === 'budget_delta_warn');
    const action = buildFinancialNextAction({
      categoryName: budgetItem?.title ?? undefined,
      usagePercent: budgetItem ? 92 : 0,
    });
    return { action, isLoading: false };
  }

  if (scope === 'schedule') {
    if (!projectId || obraHojeQ.isLoading) return { action: null, isLoading: true };
    const queue = obraHojeQ.data;
    const blockedItem = queue?.buckets
      .flatMap((b) => b.items)
      .find((i) => i.kind === 'blocked');
    const action = buildScheduleNextAction({
      nextMilestoneName: blockedItem?.title ?? undefined,
      nextMilestoneDaysLeft: blockedItem ? 3 : undefined,
    });
    return { action, isLoading: false };
  }

  if (scope === 'photos') {
    if (!projectId || obraHojeQ.isLoading) return { action: null, isLoading: true };
    const queue = obraHojeQ.data;
    // Photos without RDO surfaced as rdo_overdue items in obra-hoje
    const rdoItems = queue?.buckets
      .flatMap((b) => b.items)
      .filter((i) => i.kind === 'rdo_overdue') ?? [];
    const action = buildPhotosNextAction({
      photosWithoutRdoCount: rdoItems.length > 0 ? 12 : 0,
    });
    return { action, isLoading: false };
  }

  if (scope === 'trena') {
    if (!projectId || obraHojeQ.isLoading) return { action: null, isLoading: true };
    // Trena drafts aren't in obra-hoje; we use null result here until a
    // dedicated endpoint is wired. The builder won't fire if openDraftCount=0.
    const action = buildTrenaNextAction({
      openDraftCount: 0,
      oldestDraftAgeDays: 0,
    });
    return { action, isLoading: false };
  }

  if (scope === 'inbox') {
    if (inboxQ.isLoading) return { action: null, isLoading: true };
    const inbox = inboxQ.data;
    const pendingCount = inbox?.pending.length ?? 0;
    const action = buildInboxNextAction({ pendingDecisionsCount: pendingCount });
    return { action, isLoading: false };
  }

  return { action: null, isLoading: false };
}
